/**
 * @file        fs-basic.js
 *              Basic filesystem support for dcp-client internals, used for manipulating cross-platform
 *              (eg pythonmonkey <> nodejs) files which are used by dcp-client.
 *
 *              All interfaces are synchronous, read/write JS strings, and use utf8 on disk.
 *
 * @author      Wes Garland, wes@distributive.network
 * @date        Feb 2024
 */

const env = require('./dcp-env');

function declFun(api)
{
  exports[api] = () => { throw new Error(`fs interface ${api} not supported on platform ${env.platform}`) };
}

function declConst(api)
{
  exports[api] = { valueOf: null, source: `uninitialized constant ${api} in fs-basic for platform ${env.platform}` };
}

/* Supported APIs. */

declFun('rm');
declFun('readFile');
declFun('readSync');
declFun('writeFile');
declFun('writeFileAtomic');
declFun('fileSize');
declFun('mkdir');
declFun('rmdir');
declFun('readDir');
declFun('getMode');
declFun('fileExists');
declFun('dirExists');
declFun('pathResolve');
declFun('basename');
declFun('dirname');
declFun('absPath');
declFun('joinPath');
declConst('constants');
declConst('pathSep');
declConst('stdin');
declConst('stdout');
declConst('stderr');
Object.seal(exports);

if (env.platform === 'nodejs')
{
  const { requireNative } = require('dcp/dcp-client/webpack-native-bridge');
  const fs = requireNative('fs');
  const path = requireNative('path');
  const process = requireNative('process');

  exports.rm         = (filename) => fs.unlinkSync(filename);
  exports.readFile   = (filename) => fs.readFileSync(filename, 'utf8');
  exports.readSync   = (fd, buffer, ...options) => fs.readSync(fd, buffer, ...options);
  exports.writeFile  = (filename, string) => fs.writeFileSync(filename, string, 'utf8');
  exports.fileSize   = (filename) => fs.statSync(filename).size;
  exports.mkdir      = (dirname) => fs.mkdirSync(dirname, { recursive: true });
  exports.rmdir      = (dirname)  => fs.rmdirSync(dirname);
  exports.readDir    = (dirname)  => fs.readdirSync(dirname);
  exports.getMode    = (filename) => fs.statSync(filename).mode;
  exports.basename   = (filename) => path.basename(filename);
  exports.fileExists = (filename) => fs.existsSync(filename) && fs.statSync(filename).mode & fs.constants.S_IFREG;
  exports.dirExists  = (filename) => fs.existsSync(filename) && fs.statSync(filename).mode & fs.constants.S_IFDIR;
  exports.constants = {
    W_OK: fs.constants.W_OK,
    R_OK: fs.constants.R_OK,
  };
  exports.pathResolve = path.resolve;
  exports.dirname  = path.dirname;
  exports.absPath  = path.isAbsolute;
  exports.joinPath = path.join;
  exports.pathSep  = path.sep;
  exports.stdin    = process.stdin;
  exports.stdout   = process.stdout;
  exports.stderr   = process.stderr;

  exports.writeFileAtomic = function fsBasic$$writeFileAtomic(filename, contents)
  {
    const { createTempFile } = require('dcp/utils');
    const writeDir = exports.dirname(filename);
    const fileBasename = exports.basename(filename);
    const tempFileHnd = createTempFile(fileBasename + '-XXXXXX', false, 0o600, writeDir);
    tempFileHnd.writeSync(contents, 'utf8');
    fs.renameSync(tempFileHnd.filename, filename);
    tempFileHnd.remove(); /* not a NOP - side-effect cleans up atexit handler */
    tempFileHnd.closeSync();
  }
}
else if (env.platform === 'pythonmonkey')
{
  /* Exports are directly injected by dcp-client/index.py so that we don't have to solve the problem of
   * embedding python code elegantly into a webpack bundle. This means that we must not use fsBasic
   * during the loading of the bundle.
   */
}
